import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import ApexCharts from 'src/components/apexcharts/ApexCharts';

// A career's Skilldar for the career panel of the wide and minimal layouts: no labels on the chart, hover (or tap) it and a small
// chip names the skill nearest the pointer.
const SkilldarMini = ({ chart, tone, height }) => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const categories = (chart?.options?.xaxis?.categories || []).map((label) => String(label).toUpperCase());
  if (!chart?.series?.length || !categories.length) return null;

  const chartData = {
    // a copy: the chart library adds fields to what it is given
    series: JSON.parse(JSON.stringify(chart.series)),
    options: {
      fill: { opacity: 0.6, colors: [] },
      tooltip: { enabled: false },
      xaxis: { categories, labels: { show: false } },
      grid: { borderColor: '#3067DE' },
      plotOptions: { radar: { polygons: { strokeColors: ['#FD4645', '#FFB55B', '#2AA24A', '#933DA9', '#3067DE'] } } },
      legend: { show: false },
      yaxis: { max: 100, tickAmount: 5, min: 0, show: false },
      chart: { animations: { enabled: true }, toolbar: { show: false }, background: 'transparent' },
      markers: { size: 3, strokeColors: '#fff', strokeWidth: 2, hover: { size: 6, sizeOffset: 3 } },
    },
  };

  const showNearest = (event) => {
    const wrap = wrapRef.current;
    const canvas = wrap?.querySelector('.apexcharts-canvas');
    if (!canvas) return;
    const box = canvas.getBoundingClientRect();
    // the first skill is at the top and the rest follow clockwise
    const angle = (Math.atan2(event.clientX - (box.left + box.width / 2), -(event.clientY - (box.top + box.height / 2))) + 2 * Math.PI) % (2 * Math.PI);
    const index = Math.round(angle / ((2 * Math.PI) / categories.length)) % categories.length;
    const label = categories[index].toLowerCase();
    const wrapBox = wrap.getBoundingClientRect();
    setHover({ label: label.charAt(0).toUpperCase() + label.slice(1), x: event.clientX - wrapBox.left, y: event.clientY - wrapBox.top });
  };

  return (
    <div
      ref={wrapRef}
      role="img"
      aria-label={`Skilldar chart of: ${categories.map((c) => c.toLowerCase()).join(', ')}`}
      onPointerMove={showNearest}
      onPointerDown={showNearest}
      onPointerLeave={() => setHover(null)}
      style={{ position: 'relative', width: '100%', touchAction: 'pan-y' }}
    >
      {hover && (
        <span
          style={{
            position: 'absolute',
            left: hover.x,
            top: Math.max(hover.y - 38, 0),
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 2,
            background: tone === 'light' ? '#ffffff' : '#12151c',
            color: tone === 'light' ? '#12151c' : '#ffffff',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            padding: '7px 10px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
          }}
        >
          {hover.label}
        </span>
      )}
      <ApexCharts type="radar" width="100%" height={height || 260} chartData={chartData} />
    </div>
  );
};

SkilldarMini.propTypes = {
  chart: PropTypes.object,
  tone: PropTypes.string,
  height: PropTypes.number,
};

export default SkilldarMini;
